import React, { useContext, useState, useCallback, useRef } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import BSButton from '../../form/BSButton';
import DatePickerField from '../../form/DatePickerField';
import { isRequiredField } from '../../form/RequiredAsterisk';
import LabelField from '../../form/LabelField';
import { AccountContext } from '../../Accounts';
import { DataTypeContext } from '../../DataTypes';
import { API } from '../../Constants';
import { renderAddress, formatDate, formatTime } from '../../Utils';
import AppointmentCard from './AppointmentCard';
import AppointmentResults from './AppointmentResults';
import { useCustomTranslator } from '../../utils/customTranslator';

const BookAppointment = ({ isDelivery = false }) => {
    const { dataTypes, isDeliveryEnabled, useProvisionsAndNotes } = useContext(DataTypeContext);
    const {
        clientId,
        selectedAppointmentSlot,
        setSelectedAppointmentSlot,
        setSelectedAppointmentLocation,
        setIsDeliveryAppointment,
    } = useContext(AccountContext);
    const history = useHistory();
    const location = useLocation();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { t } = useTranslation();
    const customT = useCustomTranslator();
    const [appointmentSlots, setAppointmentSlots] = useState([]);
    const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);
    const [activeDayKey, setActiveDayKey] = useState(null);
    const [activeTimeRange, setActiveTimeRange] = useState('morning');
    const existingAppointment = location && location.state !== undefined && location.state.existingAppointment;
    const appointmentCutOffHours = dataTypes?.appointmentOptions?.appointmentCutOffHours || 48;
    const supportNotice = t('upcomingAppointments.supportNotice', { hours: appointmentCutOffHours });
    const activityId = existingAppointment && existingAppointment.id;
    const [isLoading, setLoading] = useState(false) ;
    const [provisions, setProvisions] = useState(null);
    const [isBooking, setBooking] = useState(false) ;
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [lastSearchedProgram, setLastSearchedProgram] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const useProgramSelection = dataTypes.useProgramSelection;
    let programRef = useRef();
    let locationPrograms = {};
    const locations = dataTypes.agencies.map((a) => {
        locationPrograms[a.agencyId] = a.programs;
        a['nameWithAddress'] = `${a.name} (${renderAddress(a.address)})`;
        return a;
    });

    const schema = Yup.object({
        startDate: Yup.date().required(),
        endDate: Yup.date().required(),
        agency: isDelivery ? Yup.string() : Yup.string().label(t('appointment.agency')).required(),
        program: useProgramSelection ? Yup.string().label(t('appointment.program')).required() : Yup.string(),
    });

    const findAppointments = async (values) => {
        const programId = values.program;
        const startDate = formatDate(values.startDate);
        const endDate = formatDate(values.endDate);
        setSelectedSlotIndex(null);
        setSelectedAppointmentSlot(null);
        setActiveDayKey(null);
        setActiveTimeRange('morning');
        setProvisions(null);
        setLoading(true);
        setErrorMessage('');
        if (useProgramSelection) {
            setLastSearchedProgram(programId);
        }
        let url = '';
        if (isDelivery) {
            url = `${API.AVAILABLE_DELIVERY_URL}/${dataTypes.vromoIntegrationNetwork}?startDate=${startDate}&endDate=${endDate}&clientId=${clientId}`;
        } else {
            const { agency } = values;
            setSelectedAppointmentLocation(locations.filter((l) => l.agencyId === parseInt(agency))[0]);
            const programParam = programId ? `&programId=${programId}` : '';
            url = `${API.AVAILABLE_APPOINTMENTS_URL}/${agency}?startDate=${startDate}&endDate=${endDate}${programParam}`;
        }

        const response = await fetch(url);
        const data = await response.json();

        let availableSlots = null;
        let availableProvisions = [];
        if (response.ok && data) {
            if (Array.isArray(data) && data[0] && Array.isArray(data[0].provisions)) {
                availableProvisions = data[0].provisions;
            }

            if (data && data[0] && data[0]['timeSlots']) {
                availableSlots = data[0]['timeSlots'].filter((slot) => slot['availabilityCount'] > 0 && slot['canBook']);
            } else if (data && data.length) {
                availableSlots = data.filter((slot) => slot['available'] > 0);
            }
        } else if (!response.ok && data.code) {
            const customError = customT(`api.error.${data.code}`) || data?.message || data?.error;
            setErrorMessage(customError);
        } else {
            const customError = customT('api.error.UNKNOWN');
            setErrorMessage(customError);
        }

        if (availableSlots && availableSlots.length > 0) {
            setAppointmentSlots(availableSlots);
            setProvisions(availableProvisions);
        } else {
            setAppointmentSlots(null);
            setProvisions([]);
        }
        setLoading(false);
    };

    const handleSlotSelect = (i) => {
        const appointment = appointmentSlots[i];
        setSelectedAppointmentSlot(appointment);
        setSelectedSlotIndex(i);
        setLoading(false);
    };

    const handleBookAndValidate = async (validateForm, values) => {
        try {
            setBooking(true);
            const errors = await validateForm();
            if (errors && Object.keys(errors)?.length === 0) {
                handleBookSlot(values);
            }
        } catch (err) {
            setBooking(false);
            console.log(err);
        }
    };

    const handleBookSlot = async (values) => {
        try {
            setErrorMessage('');
            const token = await executeRecaptcha('appointments');
            const payload = {
                token: token,
            };

            if (isDelivery) {
                payload['date'] = formatDate(selectedAppointmentSlot.dateObj);
                payload['time'] = formatTime(selectedAppointmentSlot.time24);
            } else {
                payload['date'] = selectedAppointmentSlot.availableDate;
                payload['time'] = selectedAppointmentSlot.availableTime;
            }

            const agency = isDelivery ? selectedAppointmentSlot?.locationId : values?.agency;

            if (!existingAppointment) {
                const findResult = await (await fetch(`${API.FIND_CLIENT_URL}/${clientId}`)).json();
                payload['clientProfileId'] = findResult.clientProfileId;
            }

            const createURL = `${API.CREATE_APPOINTMENTS_URL}/${agency}`;
            const updateURL = `${API.UPDATE_APPOINTMENTS_URL}/${agency}/${activityId}`;
            const url = existingAppointment ? updateURL : createURL;

            if (selectedAppointmentSlot?.programId || useProgramSelection) {
                payload['programId'] = selectedAppointmentSlot.programId || lastSearchedProgram;
            }

            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            })
                .then((response) =>
                    response.json().then(data => {
                        if (response.ok) {
                            setIsDeliveryAppointment(isDelivery);
                            history.push({
                                pathname: '/confirm-booking',
                                state: {
                                    isModification: Boolean(existingAppointment),
                                    existingAppointment,
                                },
                            });
                        } else if (!response.ok && data.code) {
                            const customError = customT(`api.error.${data.code}`) || data?.message || data?.error;
                            setErrorMessage(customError);
                            setBooking(false);
                        } else {
                            const customError = customT('api.error.UNKNOWN');
                            setErrorMessage(customError);
                            setBooking(false);
                        }
                    })
                )

        } catch(e) {
            console.error('problem recording appointment', e);
            const customError = customT('api.error.UNKNOWN');
            setErrorMessage(customError);
            setBooking(false);
        };

    };

    const getLabel = useCallback(
        (field) => {
            if (isDelivery) {
                return t(`delivery.${field}`);
            }

            return isDeliveryEnabled ? t(`pickup.${field}`) : t(`appointment.${field}`);
        },
        [isDelivery, isDeliveryEnabled, t]
    );

    return (
        <Formik
            validationSchema={schema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={findAppointments}
            initialValues={{ startDate: new Date(), endDate: new Date() }}
        >
            {({ handleSubmit, values, errors, setFieldValue }) => {
                const selectedOrgName = locations.find((loc) => loc.agencyId === parseInt(values.agency, 10))?.name || t('appointment.agency');
                const selectedLocationData = locations.find((loc) => loc.agencyId === parseInt(values.agency, 10));
                const selectedProgramName = selectedLocationData?.programs?.find((program) => `${program.id}` === `${values.program}`)?.name || '';
                const selectedStartDate = values.startDate
                    ? moment(values.startDate).format(dataTypes.dateFormat.toUpperCase())
                    : '';
                const selectedEndDate = values.endDate
                    ? moment(values.endDate).format(dataTypes.dateFormat.toUpperCase())
                    : '';
                const noAppointmentsMessage = t('appointment.noSlotsForSelection', {
                    org: selectedOrgName,
                    start: selectedStartDate,
                    end: selectedEndDate,
                });
                const noAppointmentsHeading = t('appointment.noSlotsHeading');

                const showNoResultsState = appointmentSlots === null && errorMessage === '';
                const showApiErrorState = errorMessage !== '';

                return (
                    <Col md={6} sm={12} className="mx-auto">
                        <Form noValidate onSubmit={handleSubmit} className="card shadow-sm rounded p-4">
                            <h4 className="mb-3 text-center">{getLabel('heading')}</h4>
                            {existingAppointment && (
                                <div className="mb-4">
                                    <div className="alert alert-warning text-center mb-3">
                                        <strong>{t('appointment.modifyMessage')}</strong>
                                    </div>
                                    <AppointmentCard appointment={existingAppointment} variant="summary" className="bg-info" />
                                </div>
                            )}

                            {!isDelivery && (
                                <Form.Group controlId='agency' className="mb-4">
                                    <LabelField
                                        label={t('appointment.agency')}
                                        isRequired={isRequiredField(schema, 'agency')}
                                    />
                                    <Select
                                        name='agency'
                                        options={(locations || []).map((type) => ({
                                            label: type.nameWithAddress,
                                            value: type.agencyId,
                                        }))}
                                        className={`appointment-select ${errors.agency ? 'appointment-select--error is-invalid' : ''}`}
                                        classNamePrefix="appointment-select"
                                        onChange={(e) => {
                                            setFieldValue('agency', e.value);
                                            if (useProgramSelection) {
                                                setFieldValue('program', '');
                                                setSelectedProgram(null);
                                                programRef.current.select.clearValue();
                                                setSelectedLocation(e.value);
                                            }
                                        }}
                                    />
                                    <Form.Control.Feedback type='invalid'>{errors.agency}</Form.Control.Feedback>
                                </Form.Group>
                            )}
                            {useProgramSelection && (
                                <Form.Group controlId="program" className="mb-4">
                                    <LabelField
                                        label={t('appointment.program')}
                                        isRequired={isRequiredField(schema, 'program')}
                                    />
                                    <Select
                                        name='program'
                                        ref={programRef}
                                        options={(locationPrograms[selectedLocation] || []).map((type) => ({
                                            label: type.name,
                                            value: type.id,
                                        }))}
                                        className={`appointment-select ${errors.program ? 'appointment-select--error is-invalid' : ''}`}
                                        classNamePrefix="appointment-select"
                                        onChange={(e) => {
                                            if (e) {
                                                setFieldValue('program', e.value);
                                                setSelectedProgram(e.value);
                                            }
                                        }}
                                    />
                                    <Form.Control.Feedback type='invalid'>{errors.program}</Form.Control.Feedback>
                                </Form.Group>
                            )}
                            {useProgramSelection && selectedLocation && selectedProgram && (
                                <Form.Group className="mb-4">
                                    <p className="text-muted mb-0">{locationPrograms[selectedLocation].find((p) => p.id === selectedProgram)?.summary}</p>
                                </Form.Group>
                            )}
                            {existingAppointment && (
                                <Form.Group className="mb-4">
                                    <p className="text-muted mb-0">{supportNotice}</p>
                                </Form.Group>
                            )}
                            <Row className="g-3 mb-4">
                                <Form.Group as={Col} md={6} controlId="startDate" className="mb-0">
                                    <LabelField
                                        label={getLabel('startDate')}
                                        isRequired={isRequiredField(schema, 'startDate')}
                                    />
                                    <DatePickerField
                                        className={!!errors.startDate ? 'form-control is-invalid' : 'form-control'}
                                        name="startDate"
                                        placeholderText={dataTypes.dateFormat.toUpperCase()}
                                        dateFormat={dataTypes.dateFormat}
                                        minDate={new Date()}
                                        autoComplete="off"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.startDate}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group as={Col} md={6} controlId="endDate" className="mb-0">
                                    <LabelField
                                        label={t('appointment.endDate')}
                                        isRequired={isRequiredField(schema, 'endDate')}
                                    />
                                    <DatePickerField
                                        className={!!errors.endDate ? 'form-control is-invalid' : 'form-control'}
                                        name="endDate"
                                        placeholderText={dataTypes.dateFormat.toUpperCase()}
                                        dateFormat={dataTypes.dateFormat}
                                        minDate={new Date()}
                                        autoComplete="off"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.endDate}</Form.Control.Feedback>
                                </Form.Group>
                            </Row>

                            <Form.Group className="text-center mb-4">
                                <Button variant="primary" type="submit" disabled={isLoading} className="px-5">
                                    {isLoading ? 'Searching..' : 'Search'}
                                </Button>
                            </Form.Group>

                            <AppointmentResults
                                appointmentSlots={appointmentSlots}
                                activeDayKey={activeDayKey}
                                setActiveDayKey={setActiveDayKey}
                                activeTimeRange={activeTimeRange}
                                setActiveTimeRange={setActiveTimeRange}
                                selectedSlotIndex={selectedSlotIndex}
                                onSlotSelect={handleSlotSelect}
                                onConfirmSelection={() => {
                                    history.push('/confirm-booking', {
                                        agency: values.agency,
                                        program: values.program,
                                        selectedProgramName,
                                        provisions,
                                        isDelivery,
                                        useProvisionsAndNotes,
                                        existingAppointment,
                                    });
                                }}
                                isBooking={false}
                                confirmLabel={t('appointment.selectTimeSlotButton')}
                                bookingLabel={t('appointment.booking')}
                            />

                            {showNoResultsState && (
                                <div className="mx-auto text-center mb-4" role="status" aria-live="polite">
                                    <div className="appointment-no-results__icon" aria-hidden="true">
                                        <span className="appointment-no-results__icon-mark">!</span>
                                    </div>
                                    <h5 className="my-4">{noAppointmentsHeading}</h5>
                                    <p className="text-secondary mb-0">
                                        {isDelivery ? customT('delivery.noSlots') : noAppointmentsMessage}
                                    </p>
                                </div>
                            )}

                            {showApiErrorState && (
                                <p className="text-center text-danger mb-4" style={{ whiteSpace: 'pre-wrap' }}>
                                    {errorMessage}
                                </p>
                            )}

                            {appointmentSlots === null && isDelivery && (
                                <Form.Group className="text-center mb-4">
                                    <BSButton as={Link} to="/book-appointment" variant="primary">
                                        {t('dashboard.bookPickup')}
                                    </BSButton>
                                </Form.Group>
                            )}

                        </Form>

                        <p className="text-center mt-3 mb-0">
                            <Link to="/dashboard">{t('dashboard.return')}</Link>
                        </p>
                    </Col>
            )}}
        </Formik>
    );
};

export default BookAppointment;
